import React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
// components
import { Header } from 'components/Modals/PlanItems/styles'
import { DrawerModal, LoaderWithOverlay } from 'components/UI'
// view components
import Form, { DashboardWidgetSettingsFormFields } from './components/Form'
import ChartLoadError from './components/ChartLoadError'
// themes
import { CloseIcon } from 'components/UI/Icons'
// graphql
import { RISK_VIEW_FILTERS_QUERY } from 'graphql/risk/queries'
// constants
import { Modals } from 'constants/modals'
import { FN_TYPE_OPTIONS } from 'components/Modals/WidgetSettingsModal/components/Form/components/FunctionFields'
// hooks
import { useApolloClient, useModalData } from 'hooks'
import { useDashboardsAPI } from 'hooks/useDashboardsAPI'
import { useChartSettings } from './useChartSettings'
// views
import { Container } from './views'
// types
import { DashboardItemType, FilterTarget, IFiltersQueryArgs, IFiltersResponse, IFilter } from 'types/graphql'
// store
import { riskViewsSelector } from 'store/reducers/riskViews'
import { updateWidget, Widget } from 'store/reducers/dashboards'
import { riskTemplatesSelector } from 'store/reducers/riskTemplates'
// utils
import { handleError } from 'utils/errors/handlers'
import { getOptionByValue, getValueFromOption } from 'utils/form'
import { omit, isNumber, startCase, toNumber, get } from 'lodash'

const PAPER_PROPS = { style: { overflow: 'hidden' } }

const DashboardWidgetSettingsDrawer: React.FC = () => {
  const dispatch = useDispatch()

  const { client } = useApolloClient()
  const { updateDashboardItem } = useDashboardsAPI()
  const { hideModal, modalData } = useModalData({ modalType: Modals.DASHBOARD_WIDGET_SETTINGS_MODAL })

  const riskViews = useSelector(riskViewsSelector)
  const riskTemplates = useSelector(riskTemplatesSelector)

  const [riskViewFilters, setRiskViewFilters] = React.useState<IFilter[]>([])

  const closeDrawer = () => {
    hideModal()
  }

  const getChartTileSettings = async (data: DashboardWidgetSettingsFormFields) => ({
    width: data.width ? toNumber(data.width) : null,
    height: data.height ? toNumber(data.height) : null,
    showTitle: data.showTitle,
    chartId: await saveChart(data)
  })

  const onFormSubmit = async (data: DashboardWidgetSettingsFormFields) => {
    try {
      const input = isChart ? await getChartTileSettings(data) : getSettingsInput(data)

      await updateDashboardItem({ id: widget.item.id, settings: input })

      dispatch(updateWidget({ id: widget.item.id, data: input }))
      closeDrawer()
    } catch (error) {
      handleError(error, 'DashboardWidgetSettingsDrawer.onFormSubmit')
    }
  }

  const getSettingsInput = (data: DashboardWidgetSettingsFormFields) => {
    return {
      ...omit(data, ['image', 'widgetId', 'widgetType', 'chartTitle', 'chartConfig']),
      ...(data.width && { width: toNumber(data.width) }),
      ...(data.height && { height: toNumber(data.height) }),
      ...(data.padding && { padding: toNumber(data.padding) }),
      ...(data.image && { imageUrl: data.image.url, imageFilename: data.image.name }),
      ...(data.filterId && { filterId: getValueFromOption(data.filterId) }),
      ...(data.riskViewId && { riskViewId: getValueFromOption(data.riskViewId) }),
      ...(data.functionType && { functionType: getValueFromOption(data.functionType) }),
      ...(data.riskTemplateId && { riskTemplateId: getValueFromOption(data.riskTemplateId) }),
      ...(isNumber(data.transparency) && { transparency: (100 - data.transparency) / 100 })
    }
  }

  const isOpen: boolean = modalData?.isOpen
  const widget: Widget = modalData?.widget

  const settings = widget?.item.settings

  const {
    isChart,
    isLoading: isChartLoading,
    loadError: chartLoadError,
    initialValues: chartValues,
    saveChart
  } = useChartSettings(widget)

  const filter = settings?.filterId && riskViewFilters.find((item) => item.id === settings?.filterId)
  const riskView = settings?.riskViewId && riskViews.find((item) => item.id === settings?.riskViewId)
  const riskTemplate = settings?.riskTemplateId && riskTemplates.find((item) => item.id === settings?.riskTemplateId)

  const initialValues = {
    ...(settings?.imageUrl && {
      image: {
        url: widget.item.settings.imageUrl,
        name: widget.item.settings.imageFilename
      }
    }),
    width: settings?.width,
    title: settings?.title,
    height: settings?.height,
    padding: settings?.padding,
    widgetId: widget?.item.id,
    filterId: filter ? { label: filter.name, value: filter.id } : undefined,
    showTitle: settings?.showTitle,
    riskViewId: riskView ? { label: riskView.name, value: riskView.id } : undefined,
    dropShadow: settings?.dropShadow,
    widgetType: startCase(widget?.item.itemType.toLowerCase()),
    description: settings?.description,
    functionType: getOptionByValue(settings?.functionType, FN_TYPE_OPTIONS),
    functionLink: settings?.functionLink,
    openInWindow: settings?.openInWindow,
    transparency: 100 - settings?.transparency * 100,
    riskTemplateId: riskTemplate ? { label: riskTemplate.name, value: riskTemplate.id } : undefined,
    ...chartValues
  }

  const getRiskViewFilters = async () => {
    try {
      const filters = get(
        await client.query<IFiltersResponse, IFiltersQueryArgs>({
          query: RISK_VIEW_FILTERS_QUERY,
          variables: {
            filter: {
              targetId: settings?.riskTemplateId,
              target: FilterTarget.RiskTemplate
            }
          }
        }),
        'data.filters.data'
      ) as IFilter[]

      setRiskViewFilters(filters)
    } catch (error) {
      handleError(error, 'DashboardWidgetSettingsDrawer.getRiskViewFilters')
    }
  }

  React.useEffect(() => {
    if (widget?.item.itemType === DashboardItemType.Card && settings?.filterId) {
      getRiskViewFilters()
    }
  }, [widget?.item.id])

  return (
    <DrawerModal width={isChart ? '640px' : '500px'} isOpen={isOpen} hideModal={hideModal} PaperProps={PAPER_PROPS}>
      <Header>
        <span>Widget Information</span>
        <CloseIcon onClick={closeDrawer} />
      </Header>
      <Container>
        {isOpen && isChartLoading ? (
          <LoaderWithOverlay isLoading height='100%' />
        ) : isOpen && chartLoadError ? (
          <ChartLoadError error={chartLoadError} onClose={closeDrawer} />
        ) : (
          <Form
            onSubmit={onFormSubmit}
            widgetId={widget?.item.id}
            onCancelClick={closeDrawer}
            initialValues={initialValues}
            widgetType={widget?.item.itemType}
          />
        )}
      </Container>
    </DrawerModal>
  )
}

export default DashboardWidgetSettingsDrawer
